import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

const ACTIVE_STATUSES = ["queued", "processing", "pending"]

export default class extends Controller {
  static targets = ["state"]
  static values = {
    hiddenInterval: { type: Number, default: 10000 },
    interval: { type: Number, default: 2000 },
    url: String
  }

  connect() {
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this)

    document.addEventListener("visibilitychange", this.handleVisibilityChange)

    if (this.shouldPoll) {
      this.schedule()
    }
  }

  disconnect() {
    this.clearTimer()
    document.removeEventListener("visibilitychange", this.handleVisibilityChange)
  }

  handleVisibilityChange() {
    if (!this.shouldPoll) {
      this.clearTimer()
      return
    }

    this.schedule()
  }

  schedule() {
    this.clearTimer()
    if (!this.shouldPoll || !this.hasUrlValue) {
      return
    }

    this.timeout = setTimeout(() => this.reload(), this.pollInterval)
  }

  async reload() {
    this.clearTimer()

    if (!this.shouldPoll || !this.hasUrlValue || this.loading) {
      return
    }

    if (document.hidden) {
      this.schedule()
      return
    }

    this.loading = true

    try {
      const response = await fetch(this.urlValue, {
        headers: {
          Accept: "text/vnd.turbo-stream.html, text/html",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin"
      })

      if (!response.ok) {
        throw new Error(`Preview poll failed with status ${response.status}`)
      }

      const html = await response.text()
      Turbo.renderStreamMessage(html)
    } catch (error) {
      console.error(error)
    } finally {
      this.loading = false
      if (this.shouldPoll) {
        this.schedule()
      }
    }
  }

  clearTimer() {
    if (!this.timeout) {
      return
    }

    clearTimeout(this.timeout)
    this.timeout = null
  }

  get pollInterval() {
    return document.hidden ? this.hiddenIntervalValue : this.intervalValue
  }

  get shouldPoll() {
    return ACTIVE_STATUSES.includes(this.status)
  }

  get status() {
    return this.hasStateTarget ? this.stateTarget.dataset.previewStatus : null
  }
}
